/**
 * The other records with a person's name (#454, 28 Oct): a doctor who is also a vendor shows his vendor records. Possible
 * matches, never merged: CE21 is read-only and Keystone merges nothing. Rows in SheetRows' layout, with a link for a label.
 */
import type { Namesake, PossibleMatch } from "../../api/audienceReview.types";
import { href } from "../app/links";
import { cx, focusRing, SheetSection, Standing } from "../kit";
import { sentence, standingOf } from "../screens/people/format";
import { acWords, roleWords } from "../screens/people/namesakes";

const LINK = cx("link-line rounded-sm text-cell-foreground", focusRing);

function Row({ match }: { match: Namesake | PossibleMatch }) {
  const ac = "activeCampaign" in match ? match.activeCampaign : "";
  const standing = standingOf(match.standing);
  return (
    <div className="flex items-baseline justify-between gap-4 text-body leading-5">
      <dt className="min-w-0 text-muted-foreground">
        {match.id === null ? (
          "ActiveCampaign only"
        ) : (
          <a href={href(`/people?person=${match.id}`)} className={LINK}>
            {roleWords(match)}
          </a>
        )}
      </dt>
      <dd className="inline-flex min-w-0 shrink-0 flex-col items-end gap-1.5 text-right font-medium text-cell-foreground">
        {match.id === null ? sentence(acWords(ac)) : standing ? <Standing value={standing} /> : null}
        {match.id !== null && ac && <span className="text-meta font-normal leading-none text-muted-foreground">In ActiveCampaign, {acWords(ac)}</span>}
      </dd>
    </div>
  );
}

function Records({ title, records }: { title: string; records: readonly (Namesake | PossibleMatch)[] }) {
  if (records.length === 0) return null;
  return (
    <SheetSection title={title}>
      <dl className="flex w-full flex-col gap-3.5">
        {records.map((record, i) => (
          <Row key={record.id ?? `ac-${i}`} match={record} />
        ))}
      </dl>
    </SheetSection>
  );
}

/** Every person page: the other CE21 records with this name, shown only when there is one. */
export const SameName = ({ namesakes }: { namesakes: readonly Namesake[] }) => <Records title="Same name in CE21" records={namesakes} />;

/** A held person: the records the hold compared them with, CE21's and ActiveCampaign's. The reason is under the name. */
export const PossibleMatches = ({ matches }: { matches: readonly PossibleMatch[] }) => <Records title="Possible matches" records={matches} />;
